import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getDashboardSummary, getEmployeeStats, getFinancialSummary } from '../lib/api';
import {
  Users,
  Briefcase,
  ListChecks,
  Clock,
  CurrencyDollar,
  TrendUp,
  TrendDown,
  CheckCircle,
  XCircle,
  Storefront,
  Receipt,
  ChartLine,
  ArrowRight,
} from '@phosphor-icons/react';
import { Progress } from '../components/ui/progress';

function StatCard({ icon: Icon, label, value, subValue, trend, color = 'primary' }) {
  const colors = {
    primary: 'from-[#E2B05E]/20 to-[#E2B05E]/5 border-[#E2B05E]/30',
    secondary: 'from-[#3AB8D6]/20 to-[#3AB8D6]/5 border-[#3AB8D6]/30',
    success: 'from-emerald-500/20 to-emerald-500/5 border-emerald-500/30',
    warning: 'from-amber-500/20 to-amber-500/5 border-amber-500/30',
    error: 'from-red-500/20 to-red-500/5 border-red-500/30',
  };

  const iconColors = {
    primary: 'text-[#E2B05E]',
    secondary: 'text-[#3AB8D6]',
    success: 'text-emerald-400',
    warning: 'text-amber-400',
    error: 'text-red-400',
  };

  return (
    <div className={`valhalla-card p-5 bg-gradient-to-br ${colors[color]}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2.5 rounded-lg bg-[#0B0E14]/50 ${iconColors[color]}`}>
          <Icon weight="duotone" className="w-5 h-5" />
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend >= 0 ? <TrendUp className="w-3 h-3" /> : <TrendDown className="w-3 h-3" />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">{label}</p>
      <p className="font-['Outfit'] text-2xl font-bold text-[#F3F4F6]">{value}</p>
      {subValue && <p className="text-xs text-[#9CA3AF] mt-1">{subValue}</p>}
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, color = 'primary' }) {
  const colors = {
    primary: 'hover:border-[#E2B05E] hover:text-[#E2B05E]',
    secondary: 'hover:border-[#3AB8D6] hover:text-[#3AB8D6]',
  };

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 p-4 rounded-xl border border-[#242E42] bg-[#151B26] text-[#9CA3AF] transition-all duration-200 ${colors[color]}`}
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{label}</span>
      <ArrowRight className="w-4 h-4 ml-auto" />
    </button>
  );
}

export function DashboardPage() {
  const { t } = useLanguage();
  const { user, isManager } = useAuth();
  const [summary, setSummary] = useState(null);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [financials, setFinancials] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [summaryRes, statsRes] = await Promise.all([
          getDashboardSummary(),
          getEmployeeStats(),
        ]);
        setSummary(summaryRes.data);
        setEmployeeStats(statsRes.data);

        if (isManager) {
          const financialsRes = await getFinancialSummary();
          setFinancials(financialsRes.data);
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isManager]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <Layout title={`${t('dashboard')} - ${t('welcomeBack')}, ${user?.name?.split(' ')[0] || 'User'}`}>
      <div className="space-y-6 stagger-children" data-testid="dashboard">
        {/* Main Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={Users}
            label={t('totalEmployees')}
            value={summary?.employees?.total_active || 0}
            color="primary"
          />
          <StatCard
            icon={Briefcase}
            label={t('activeProjects')}
            value={summary?.projects?.active || 0}
            subValue={`${summary?.projects?.total || 0} total`}
            color="secondary"
          />
          <StatCard
            icon={ListChecks}
            label={t('pendingTasks')}
            value={summary?.tasks?.pending || 0}
            subValue={`${summary?.tasks?.completed || 0} ${t('completedTasks').toLowerCase()}`}
            color="warning"
          />
          <StatCard
            icon={Storefront}
            label={t('marketplace')}
            value={summary?.tasks?.marketplace_available || 0}
            subValue={t('availableTasks')}
            color="success"
          />
        </div>

        {/* Employee Personal Stats */}
        {employeeStats && !employeeStats.error && (
          <div className="valhalla-card p-6">
            <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6] mb-4">
              {t('hoursThisMonth')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">{t('hours')}</p>
                <p className="font-['Outfit'] text-3xl font-bold text-[#E2B05E]">
                  {employeeStats.period_hours?.toFixed(1) || 0}
                </p>
                <p className="text-sm text-[#9CA3AF] mt-1">
                  / {employeeStats.weekly_capacity * 4}h capacity
                </p>
                <Progress 
                  value={(employeeStats.period_hours / (employeeStats.weekly_capacity * 4)) * 100} 
                  className="h-2 mt-2 bg-[#242E42]"
                />
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">{t('completedTasks')}</p>
                <p className="font-['Outfit'] text-3xl font-bold text-emerald-400">
                  {employeeStats.completed_tasks || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">Active Tasks</p>
                <p className="font-['Outfit'] text-3xl font-bold text-[#3AB8D6]">
                  {employeeStats.active_tasks || 0}
                </p>
              </div>
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-2">{t('rejectedTasks')}</p>
                <p className="font-['Outfit'] text-3xl font-bold text-red-400">
                  {employeeStats.rejected_tasks || 0}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Financial KPIs - Manager Only */}
        {isManager && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              icon={Receipt}
              label={t('totalInvoiced')}
              value={formatCurrency(financials?.invoiced)}
              color="primary"
            />
            <StatCard
              icon={CurrencyDollar}
              label={t('totalCollected')}
              value={formatCurrency(financials?.collected)}
              color="success"
            />
            <StatCard
              icon={Clock}
              label={t('pendingPayroll')}
              value={formatCurrency(financials?.payroll)}
              color="warning"
            />
            <StatCard
              icon={ChartLine}
              label={t('profitability')}
              value={formatCurrency(financials?.projected_profit)}
              color={financials?.projected_profit >= 0 ? 'success' : 'error'}
            />
          </div>
        )}

        {/* Time & Approvals */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Time Tracking Summary */}
          <div className="valhalla-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">
                {t('timeTracking')}
              </h3>
              <Clock weight="duotone" className="w-5 h-5 text-[#9CA3AF]" />
            </div>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]/50">
                <div className="flex items-center gap-3">
                  <CheckCircle weight="duotone" className="w-5 h-5 text-emerald-400" />
                  <span className="text-[#F3F4F6]">{t('approved')}</span>
                </div>
                <span className="font-['Outfit'] font-bold text-[#F3F4F6]">
                  {summary?.time_tracking?.total_approved_hours?.toFixed(1) || 0}h
                </span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]/50">
                <div className="flex items-center gap-3">
                  <Clock weight="duotone" className="w-5 h-5 text-amber-400" />
                  <span className="text-[#F3F4F6]">{t('pendingApprovals')}</span>
                </div>
                <span className="font-['Outfit'] font-bold text-amber-400">
                  {summary?.time_tracking?.pending_approval || 0}
                </span>
              </div>
            </div>
          </div>

          {/* Accounts Receivable */}
          {isManager && (
            <div className="valhalla-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">
                  {t('accountsReceivable')}
                </h3>
                <Receipt weight="duotone" className="w-5 h-5 text-[#9CA3AF]" />
              </div>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]/50">
                  <span className="text-[#F3F4F6]">{t('pending')}</span>
                  <span className="font-['Outfit'] font-bold text-amber-400">
                    {formatCurrency(summary?.financials?.accounts_receivable)}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]/50">
                  <span className="text-[#F3F4F6]">{t('overdue')}</span>
                  <span className="font-['Outfit'] font-bold text-red-400">
                    {formatCurrency(financials?.overdue?.amount)} ({financials?.overdue?.count || 0})
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Clients Summary */}
        {isManager && (
          <div className="valhalla-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-['Outfit'] text-lg font-semibold text-[#F3F4F6]">
                {t('clients')}
              </h3>
            </div>
            <div className="flex items-center gap-8">
              <div>
                <p className="text-xs text-[#9CA3AF] uppercase tracking-wider mb-1">Active</p>
                <p className="font-['Outfit'] text-3xl font-bold text-[#3AB8D6]">
                  {summary?.clients?.total_active || 0}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default DashboardPage;
